import React, { useEffect, useState } from 'react';
import {
  Grid,
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  makeStyles,
} from '@material-ui/core';
import {
  Header,
  Page,
  Content,
  InfoCard,
} from '@backstage/core-components';
import { configApiRef, identityApiRef, useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { Link as RouterLink } from 'react-router-dom';
import AddIcon from '@material-ui/icons/Add';
import MenuBookIcon from '@material-ui/icons/MenuBook';
import NotificationsIcon from '@material-ui/icons/Notifications';
import { PlatformOverviewCard } from './PlatformOverviewCard';
import { DeptDashboardCard, useUserDepts } from './DeptDashboardCard';
import { AnnouncementsBanner } from './AnnouncementsBanner';

const useStyles = makeStyles(theme => ({
  statCard: {
    textAlign: 'center',
    padding: theme.spacing(2),
  },
  statValue: {
    fontSize: '2.5rem',
    fontWeight: 700,
    color: theme.palette.primary.main,
  },
  statLabel: {
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
  },
  actionCard: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
  grafanaFrame: {
    border: 'none',
    borderRadius: 4,
    width: '100%',
    height: 1200,
    overflow: 'hidden' as const,
  },
}));

interface CatalogStats {
  services: number;
  websites: number;
  databases: number;
  total: number;
}

function useCatalogStats(): CatalogStats {
  const catalogApi = useApi(catalogApiRef);
  const [stats, setStats] = useState<CatalogStats>({
    services: 0,
    websites: 0,
    databases: 0,
    total: 0,
  });

  useEffect(() => {
    let cancelled = false;

    async function fetch() {
      try {
        const [svcResp, webResp, dbResp] = await Promise.all([
          catalogApi.getEntities({
            filter: { kind: 'component', 'spec.type': 'service' },
            fields: ['metadata.name'],
          }),
          catalogApi.getEntities({
            filter: { kind: 'component', 'spec.type': 'website' },
            fields: ['metadata.name'],
          }),
          catalogApi.getEntities({
            filter: { kind: 'resource', 'spec.type': 'database' },
            fields: ['metadata.name'],
          }),
        ]);

        if (!cancelled) {
          const services = svcResp.items.length;
          const websites = webResp.items.length;
          const databases = dbResp.items.length;
          setStats({
            services,
            websites,
            databases,
            total: services + websites + databases,
          });
        }
      } catch {
        // ignore – stats will show 0
      }
    }

    fetch();
    return () => {
      cancelled = true;
    };
  }, [catalogApi]);

  return stats;
}

function StatCard({ value, label }: { value: number; label: string }) {
  const classes = useStyles();
  return (
    <Card variant="outlined" className={classes.statCard}>
      <CardContent>
        <Typography className={classes.statValue}>{value}</Typography>
        <Typography className={classes.statLabel} variant="subtitle1">
          {label}
        </Typography>
      </CardContent>
    </Card>
  );
}

function ActionCard({
  title,
  description,
  to,
  icon,
}: {
  title: string;
  description: string;
  to: string;
  icon: React.ReactNode;
}) {
  const classes = useStyles();
  return (
    <Card variant="outlined" className={classes.actionCard}>
      <CardContent>
        <Grid container spacing={1} alignItems="center">
          <Grid item>{icon}</Grid>
          <Grid item>
            <Typography variant="h6">{title}</Typography>
          </Grid>
        </Grid>
        <Typography variant="body2" color="textSecondary">
          {description}
        </Typography>
      </CardContent>
      <CardActions>
        <Button
          component={RouterLink}
          to={to}
          size="small"
          color="primary"
        >
          Open
        </Button>
      </CardActions>
    </Card>
  );
}

// Grafana iframe 폐기(성능) → 네이티브 PlatformOverviewCard 전용.
// props는 호출부 호환을 위해 시그니처만 유지(미사용).
function PlatformDashboardSection(_props: {
  grafanaBaseUrl?: string;
  grafanaFrameClass?: string;
}) {
  return (
    <InfoCard title="Platform Overview">
      <PlatformOverviewCard />
    </InfoCard>
  );
}

export const HomePage = () => {
  const classes = useStyles();
  const stats = useCatalogStats();
  const configApi = useApi(configApiRef);
  const identityApi = useApi(identityApiRef);
  const grafanaBaseUrl =
    configApi.getOptionalString('app.grafana.baseUrl') ?? '';

  // 사용자 역할 판정
  const [isPlatformAdmin, setIsPlatformAdmin] = useState(false);
  useEffect(() => {
    async function check() {
      try {
        const identity = await identityApi.getBackstageIdentity();
        const refs = new Set(
          (identity.ownershipEntityRefs ?? []).map(r => r.toLowerCase()),
        );
        setIsPlatformAdmin(refs.has('group:default/platform-admins'));
      } catch {
        // ignore
      }
    }
    check();
  }, [identityApi]);

  // 사용자 소속 부서 목록
  const userDepts = useUserDepts();

  return (
    <Page themeId="home">
      <Header title="Arcus Platform" subtitle="Internal Developer Platform" />
      <Content>
        <Grid container spacing={3}>
          {/* Catalog Stats */}
          <Grid item xs={6} sm={3}>
            <StatCard value={stats.services} label="Services" />
          </Grid>
          <Grid item xs={6} sm={3}>
            <StatCard value={stats.websites} label="Websites" />
          </Grid>
          <Grid item xs={6} sm={3}>
            <StatCard value={stats.databases} label="Databases" />
          </Grid>
          <Grid item xs={6} sm={3}>
            <StatCard value={stats.total} label="Total" />
          </Grid>

          {/* Announcements Banner */}
          <Grid item xs={12}>
            <AnnouncementsBanner />
          </Grid>

          {/* Quick Actions */}
          <Grid item xs={12} sm={4}>
            <ActionCard
              title="Create Service"
              description="Start a new service from a card template"
              to="/create"
              icon={<AddIcon color="primary" />}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <ActionCard
              title="Docs"
              description="Browse technical documentation"
              to="/docs"
              icon={<MenuBookIcon color="primary" />}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <ActionCard
              title="Notifications"
              description="View alerts and CI/CD notifications"
              to="/notifications"
              icon={<NotificationsIcon color="primary" />}
            />
          </Grid>

          {/* Platform Dashboard — platform-admin만 표시 */}
          {isPlatformAdmin && (
            <Grid item xs={12}>
              <PlatformDashboardSection
                grafanaBaseUrl={grafanaBaseUrl}
                grafanaFrameClass={classes.grafanaFrame}
              />
            </Grid>
          )}

          {/* Department Dashboard — 소속 부서별 표시 */}
          {userDepts.map(dept => (
            <Grid item xs={12} key={dept.code}>
              <DeptDashboardCard dept={dept} />
            </Grid>
          ))}
        </Grid>
      </Content>
    </Page>
  );
};
